import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { X, Download, Smartphone, Share, MoreVertical } from 'lucide-react';

/**
 * PWA Install Prompt Component
 * Shows installation instructions for mobile users on /admin pages
 */
const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already in standalone mode (already installed)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Check if previously dismissed
    const wasDismissed = localStorage.getItem('pwa_install_dismissed');
    if (wasDismissed) {
      const dismissedAt = new Date(wasDismissed);
      const daysSinceDismissed = (Date.now() - dismissedAt.getTime()) / (1000 * 60 * 60 * 24);
      // Show again after 7 days
      if (daysSinceDismissed < 7) {
        setDismissed(true);
      }
    }

    // Detect device type
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
    const isAndroidDevice = /android/.test(userAgent);
    const isMobile = isIOSDevice || isAndroidDevice;

    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);

    // Only show on mobile devices, on /admin pages, not in standalone mode
    if (isMobile && !standalone && window.location.pathname.startsWith('/admin')) {
      // For Android: Listen for beforeinstallprompt
      const handleBeforeInstall = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setShowPrompt(true);
      };

      window.addEventListener('beforeinstallprompt', handleBeforeInstall);

      // For iOS: Show instructions after a short delay
      if (isIOSDevice) {
        setTimeout(() => setShowPrompt(true), 2000);
      }

      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      };
    }
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Android install flow
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('pwa_install_dismissed', new Date().toISOString());
  };

  // Don't show if already installed, dismissed, or not applicable
  if (isStandalone || dismissed || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 bg-black/50 animate-in fade-in duration-300">
      <Card className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-300" data-testid="pwa-install-prompt">
        {/* Header */}
        <div className="bg-gradient-to-r from-tp-primary to-tp-primary/90 text-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Smartphone className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Installer l'application</h3>
                <p className="text-sm text-white/80">Accès rapide à votre back-office</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDismiss}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {isIOS ? (
            // iOS Installation Instructions
            <div className="space-y-4">
              <p className="text-sm text-tp-text-light">
                Installez Talent Partner Admin sur votre écran d'accueil pour un accès rapide :
              </p>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="p-2 bg-tp-primary/10 rounded-lg">
                    <Share className="h-5 w-5 text-tp-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-tp-text">1. Appuyez sur Partager</p>
                    <p className="text-xs text-tp-text-light">En bas de Safari</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="p-2 bg-tp-primary/10 rounded-lg">
                    <Download className="h-5 w-5 text-tp-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-tp-text">2. "Sur l'écran d'accueil"</p>
                    <p className="text-xs text-tp-text-light">Faites défiler et appuyez</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="p-2 bg-tp-primary/10 rounded-lg">
                    <Smartphone className="h-5 w-5 text-tp-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-tp-text">3. Appuyez sur "Ajouter"</p>
                    <p className="text-xs text-tp-text-light">C'est prêt !</p>
                  </div>
                </div>
              </div>
            </div>
          ) : isAndroid ? (
            // Android Installation
            <div className="space-y-4">
              <p className="text-sm text-tp-text-light">
                Installez l'application pour un accès rapide à votre back-office, même hors ligne.
              </p>
              
              <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <Download className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-green-800">Installation rapide</p>
                  <p className="text-xs text-green-600">Un seul clic suffit</p>
                </div>
              </div>

              {deferredPrompt ? (
                <Button
                  onClick={handleInstall}
                  className="w-full bg-tp-primary hover:bg-tp-primary-light text-white"
                  data-testid="pwa-install-button"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Installer maintenant
                </Button>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-tp-text-light">
                    Appuyez sur le menu de votre navigateur :
                  </p>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <MoreVertical className="h-5 w-5 text-tp-text-light" />
                    <p className="text-sm text-tp-text">Menu → "Installer l'application"</p>
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {/* Benefits */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs text-tp-text-light mb-2">Avantages :</p>
            <div className="flex flex-wrap gap-2">
              <span className="px-2 py-1 bg-tp-primary/10 text-tp-primary text-xs rounded-full">
                Accès rapide
              </span>
              <span className="px-2 py-1 bg-tp-primary/10 text-tp-primary text-xs rounded-full">
                Notifications
              </span>
              <span className="px-2 py-1 bg-tp-primary/10 text-tp-primary text-xs rounded-full">
                Plein écran
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
          <Button
            variant="ghost"
            onClick={handleDismiss}
            className="w-full text-tp-text-light hover:text-tp-text"
          >
            Plus tard
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default PWAInstallPrompt;
